import React, { FC, ReactElement, useState } from "react";
import styled from "styled-components";
import { ContentCount, CookieContentProps, CookieItemProps } from "./styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";

const Container = styled.div`
  border: 1px solid #e0e3dd;
  border-radius: 8px;
  padding: 16px;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media screen and (max-width: 500px) {
    display: block;
  }
`;

const FlexCenter = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: ${({ active }) => (active ? "#7cc242" : "#323330")};
  font-size: 12px;
  cursor: pointer;
`;

const Title = styled.h5<{ active: boolean }>`
  padding: 0;
  margin: 0 0 0 12px;
  font-family: var(--branding-font), sans-serif;
  font-size: 14px;
  line-height: 18px;
  font-weight: 700;
  color: ${({ active }) => (active ? "#7cc242" : "#323330")}; ;
`;

const AboutLink = styled.a`
  color: #7cc242;
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;
`;

const LinkText = styled.span`
  margin-right: 6px;
`;

const Content = styled.div<{ active: boolean; count: number }>`
  overflow: hidden;
  transition: 0.4s max-height;
  ${({ active, count }) => (active ? `` : "max-height: 0;")};
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: #e0e3dd;
  margin: 18px 0 16px 0;
`;

const Icon = styled(FontAwesomeIcon)<{ active?: boolean }>`
  transform: rotateZ(${({ active }) => (active ? "180" : 0)}deg);
  transition: 0.25s transform;
`;

const Provider: FC<CookieContentProps & { provider: string }> = ({ items, link, provider, title }) => {
    const [show, setShow] = useState<boolean>(false);
    return (
        <Container>
            <Header>
                <FlexCenter active={show} onClick={() => setShow(!show)}>
                    <Icon active={show} icon={faChevronDown} />
                    <Title active={show}>{title}</Title>
                    <ContentCount active={show}>{items.length}</ContentCount>
                </FlexCenter>
                <AboutLink href={link} target={"_blank"}>
                    <LinkText>{provider}</LinkText>
                    <FontAwesomeIcon icon={faExternalLink} />
                </AboutLink>
            </Header>
            <Content active={show} count={items.length}>
                <Divider />
                {items.map((o, i) => (
                    <Item key={i} title={o.title} description={o.description} expiry={o.expiry} type={o.type} />
                ))}
            </Content>
        </Container>
    );
};

const ItemContainer = styled.div`
	background-color: #f6f7f6;
	border-radius: 8px;
	padding: 8px 16px;
	&:not(:last-child) {
		margin-bottom: 8px;
	}
`;

const ItemTitle = styled.h6`
  color: #323330;
  font-size: 12px;
  line-height: 18px;
  padding: 0;
  margin: 0;
  font-weight: 700;
`;

const ItemParagraph = styled.p`
  font-size: 12px;
  line-height: 18px;
  color: #323330;
  margin: 4px 0 0 0;
  padding: 0;
`;

const ItemDivider = styled.div`
  height: 1px;
  width: 100%;
  background-color: #e0e3dd;
  margin: 4px 0;
`;

const MetaItem = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #323330;
  font-weight: 400;
  display: inline-block;
  &:not(:last-child) {
    margin-right: 24px;
  }
`;

const MetaBold = styled.span`
  color: #323330;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
`;

const Item = (props: CookieItemProps): ReactElement => {
    return (
        <ItemContainer>
            <ItemTitle>{props.title}</ItemTitle>
            <ItemParagraph>{props.description}</ItemParagraph>
            <ItemDivider/>
            <div>
                <MetaItem>
                    {props.expiry.label}: <MetaBold>{props.expiry.value}</MetaBold>
                </MetaItem>
                <MetaItem>
                    {props.type.label}: <MetaBold>{props.type.value}</MetaBold>
                </MetaItem>
            </div>
        </ItemContainer>
    );
};

export default Provider;
